import {
  AbstractType,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  InjectionToken,
  Injector,
  OnInit,
  Type,
} from '@angular/core';
import { Router } from '@angular/router';
import { lazyInject } from '@app/containers/base/decorators';
import { DecimalNumber, decimalNumber } from '@app/shared/common';
import { NumberFormatService, OrdersService } from '@app/shared/services';
import {
  ENTITY_TYPES,
  FilterModel,
  FilterOptionType,
  IApiListResponse,
} from '@app/shared/types';
import { ORDER_STATUS_VALUE } from '@app/shared/types/enums/order-status.enum';
import { UiActions } from '@app/store/actions';
import { TIME_FORMATS } from '@config/index';
import {
  filter,
  find,
  first,
  forEach,
  groupBy,
  isEmpty,
  isFunction,
  map,
  reduce,
} from 'lodash';
import { withDndGlobalLoader } from '../xux-dnd/decorators/xux-dnd-loader.decorators';
import { toMoment } from '@shared/utils';
import { I18n } from '@app/i18n/strings-keys';
import { XVII18nService } from '@app/i18n/services';

interface IOrderInfo {
  count?: number;
  text?: string;
  totalCost?: DecimalNumber | string;
  total_cost?: DecimalNumber;
  status?: string;
  delivery_date?: string;
  showNegativeCls?: boolean;
}

@Component({
  selector: 'order-summary',
  templateUrl: 'order-summary.component.html',
  styleUrls: ['./order-summary.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrderSummaryComponent implements OnInit {
  @lazyInject(UiActions) public uiActions: UiActions;
  @lazyInject(NumberFormatService)
  public numberFormatService: NumberFormatService;
  @lazyInject(Router) public router: Router;
  @lazyInject(OrdersService) public ordersService: OrdersService;
  @lazyInject(XVII18nService) public i18nService: XVII18nService;

  public summaryList: IOrderInfo[] = [];
  public last7Days: string;

  public constructor(
    public injector: Injector,
    private cdr: ChangeDetectorRef,
  ) {}

  public ngOnInit(): void {
    this.last7Days = this.i18nService.instant(I18n.InfoMessages.LastXDays, {
      lastDaysCount: 7,
    });
    this.summaryList = [
      {
        status: ORDER_STATUS_VALUE.Open,
        text: this.i18nService.instant(
          I18n.Components.LandingPageComponent.OpenAndPending,
        ),
        count: 0,
        totalCost: decimalNumber(0),
      },
      {
        status: ORDER_STATUS_VALUE.Completed,
        text: this.i18nService.instant(I18n.Status.Completed),
        count: 0,
        totalCost: decimalNumber(0),
      },
      {
        status: ORDER_STATUS_VALUE.Rejected,
        text: this.i18nService.instant(I18n.Status.Rejected),
        count: 0,
        totalCost: decimalNumber(0),
      },
      {
        status: ORDER_STATUS_VALUE.Canceled,
        text: this.i18nService.instant(I18n.Status.Canceled),
        count: 0,
        totalCost: decimalNumber(0),
      },
    ];
    this.getOrderData();
  }

  public inject<T>(
    token: Type<T> | InjectionToken<T> | AbstractType<T>,
    notFoundValue?: T,
  ): T {
    if (isFunction(this.injector.get)) {
      return this.injector?.get(token, notFoundValue);
    }

    return notFoundValue;
  }

  public gotoOrderPage = (status: string): void => {
    this.updateOrderListViewData(status);
    this.router.navigateByUrl(this.ordersService.config.uiRoute);
  };

  private updateOrderListViewData(status: string): void {
    const type: FilterOptionType = 'containsAny';
    let filterModel: FilterModel = null;
    if (status === ORDER_STATUS_VALUE.Open) {
      const filterStatus: string[] = [
        ORDER_STATUS_VALUE.Open,
        ORDER_STATUS_VALUE.Pending,
      ];
      filterModel = {
        status: { type, filter: filterStatus, headerText: filterStatus },
      };
    } else {
      const dateFrom = toMoment().startOf('day').subtract(6, 'days');
      const dateTo = toMoment().endOf('day');
      filterModel = {
        status: { type, filter: [status], headerText: status },
        delivery_date: {
          dateFrom: dateFrom.toISOString(),
          dateTo: dateTo.toISOString(),
          type: 'inRangeDate',
          headerText: `${dateFrom.format(TIME_FORMATS.L)} - ${dateTo.format(TIME_FORMATS.L)}`,
        },
      };
    }
    this.uiActions.restoreDefaultListView(ENTITY_TYPES.ORDER);
    this.uiActions.updateListViewData({
      filterModel,
      entityType: ENTITY_TYPES.ORDER,
    });
  }

  @withDndGlobalLoader('purchase_orders')
  private getOrderData(): Promise<void> {
    // Get open and pending order data
    return new Promise(async (resolve, reject) => {
      try {
        const orderReps: IApiListResponse = await this.ordersService
          .fetchList(
            {
              select: ['status', 'total_cost', 'delivery_date'],
              filter: 'status eq in(open,pending)',
            },
            {
              noLoader: true,
              showErrorMessage: true,
            },
          )
          .toPromise();

        const openOrder = first(
          filter(
            this.summaryList,
            (item) => item.status === ORDER_STATUS_VALUE.Open,
          ),
        );
        openOrder.count = orderReps.total;
        let total = decimalNumber(0);
        forEach(orderReps.items, (item) => {
          total = total.add(item.total_cost || 0);
        });
        openOrder.showNegativeCls = total.lt(0);
        openOrder.totalCost = this.numberFormatService.getNumberFormatter(true)(
          {
            value: total,
          },
        );

        this.cdr.detectChanges();
        // Get other status order in 7 days
        const dateFrom = toMoment()
          .startOf('day')
          .subtract(6, 'days')
          .toISOString();
        const dateTo = toMoment().endOf('day').toISOString();
        const purchaseOrderDetail: IApiListResponse = await this.ordersService
          .fetchList(
            {
              select: ['status', 'total_cost', 'delivery_date'],
              filter: `status eq in(completed,rejected,canceled) and delivery_date ge ${dateFrom} and delivery_date lt ${dateTo}`,
            },
            {
              noLoader: true,
              showErrorMessage: true,
            },
          )
          .toPromise();
        this.transformOrderData(purchaseOrderDetail.items as IOrderInfo[]);
        this.cdr.detectChanges();
        resolve();
      } catch (error) {
        reject(error);
      }
    });
  }

  private transformOrderData(orders: IOrderInfo[]): void {
    const groupData = groupBy(orders, 'status');
    map(groupData, (item, key) => {
      const total = reduce(
        item,
        (sum, n) => {
          return sum.add(n.total_cost || 0);
        },
        decimalNumber(0),
      );
      const summaryOrder = find(
        this.summaryList,
        (summary) => summary.status === key,
      );

      if (!isEmpty(summaryOrder)) {
        summaryOrder.count = item.length;
        summaryOrder.showNegativeCls = total.lt(0);
        summaryOrder.totalCost = this.numberFormatService.getNumberFormatter(
          true,
        )({ value: total });
      }
    });
  }
}
